// Keys with a fixed tag identity / fallback (or handled specially, like
// `title` and `canonical`). Anything else on the API `meta` object is passed
// through generically as a <meta> tag (see `extraTags`).
const KNOWN_KEYS = [
  'title', // document <title>
  'canonical', // <link rel="canonical">
  'description',
  'keywords',
  'og_title',
  'og_description',
]

// `og_*` keys -> <meta property="og:*">, everything else -> <meta name="key">
const toTag = (key, content) =>
  key.startsWith('og_')
    ? { property: `og:${key.slice(3)}`, content }
    : { name: key, content }

// Build a vue-meta `metaInfo` from the campaign API `meta` payload, falling back
// to the page's own title/description when `meta` is null / empty.
export const buildMetaInfo = ({ meta, title, description }) => {
  const hasApiMeta = meta && Object.keys(meta).length > 0
  if (!hasApiMeta) {
    return {
      title,
      meta: [{ name: 'description', content: description }],
    }
  }

  const resolvedTitle = meta.title ?? title
  const resolvedDescription = meta.description ?? description

  // Known tags are ALWAYS present; when the API omits the key the content is
  // `undefined` (not dropped, not coerced to '').
  const knownTags = [
    { name: 'description', content: resolvedDescription },
    { name: 'keywords', content: meta.keywords },
    { property: 'og:title', content: meta.og_title ?? resolvedTitle },
    {
      property: 'og:description',
      content: meta.og_description ?? resolvedDescription,
    },
  ]

  // Future-proof: any extra keys the API adds (robots, og_image, ...) get
  // emitted too.
  const extraTags = Object.keys(meta)
    .filter(key => !KNOWN_KEYS.includes(key))
    .map(key => toTag(key, meta[key]))

  const metaInfo = {
    title: resolvedTitle,
    meta: [...knownTags, ...extraTags],
  }

  // `canonical` is a <link rel="canonical" href="...">, not a <meta>.
  if (meta.canonical) {
    metaInfo.link = [{ rel: 'canonical', href: meta.canonical }]
  }

  return metaInfo
}
